import { ModalsContext } from '@/contexts/modals';
import { useAuaState } from '@/hooks/useAuaState';
import { Box, Flex, font } from '@lemonade-hq/cdk';
import type { Locale } from '@lemonade-hq/lemonation';
import { Button, InputText, LegacyDialog, MEDIA_QUERIES, TextArea } from '@lemonade-hq/nuis';
import { getLocaleFromHtml, useAnalyticsContext, useNavContext } from '@lemonade-hq/nuiza';
import React, { useContext, useCallback, useMemo } from 'react';
import styled from 'styled-components';

const MainTitle = styled(Box)`
  text-transform: uppercase;
  text-align: center;
  ${font('main', { fontSize: '1.6rem', lineHeight: '1.5', fontWeight: 700 })};
`;

const ThankYouTitle = styled(Box)`
  text-align: center;
  ${font('alt', { fontSize: '3.6rem', lineHeight: '1.3' })};
`;

const SubTitle = styled(Box)`
  text-align: center;
  ${font('main', { fontSize: '1.6rem', lineHeight: '1.3' })};
`;

const CTAButton = styled(Flex)`
  position: absolute;
  bottom: 0;
  right: 20px;
  background: #b4b3b3;
  color: #434343;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 700;
  font-family:
    system-ui,
    -apple-system,
    'Segoe UI',
    Roboto,
    Oxygen-Sans,
    Ubuntu,
    Cantarell,
    'Helvetica Neue',
    Arial,
    sans-serif;

  &:hover {
    cursor: pointer;
  }

  svg {
    left: -5px;
    position: relative;
    color: #434343;
    fill: #434343;
  }

  span {
    padding-left: 2px;
    font-size: 1.6rem;
  }

  ${MEDIA_QUERIES.desktop`
        min-width: 110px;
    `}

  ${MEDIA_QUERIES.tablet`
        width: 51px;
        height: 51px;

        span {
            display: none;
        }

        svg {
            left: unset;
            position: static;
        }
    `}

    ${MEDIA_QUERIES.mobile`
        width: 51px;
        height: 51px;

        span {
            display: none;
        }

        svg {
            left: unset;
            position: static;
        }
    `}
`;

const ButtonWrapper = styled(Box)`
  position: fixed;
  position: sticky;
  right: 0;
  bottom: 10px;
  z-index: 20;
`;

const QuestionInput = styled(TextArea)`
  height: 83px;
`;

const PinkButton = styled(Button).attrs(props => ({
  height: 47,
  minWidth: 157,
  hugging: false,
  flavor: 'primary',
  disabled: props.disabled,
}))`
  ${MEDIA_QUERIES.mobile`
        width: 100%;
    `}
`;

const ErrorContainer = styled(Box)`
  color: ${({ theme }) => theme.colors.primary};
  ${font('main', { fontSize: '1.6rem', lineHeight: '1.5' })};
  position: relative;
  text-align: center;
  margin-bottom: 10px;
  font-size: 14px;
  &:before {
    content: '';
    width: 16px;
    height: 16px;
    display: inline-block;
    text-align: center;
    background-image: url(data:image/svg+xml;base64,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);
    position: relative;
    top: 3px;
    left: -3px;
  }
`;

export interface AskUsAnythingProps {
  product?: string;
  page?: string;
}

type TranslationContent = {
  ctaLabel: string;
  title: string;
  fields: {
    name: string;
    email: string;
    message: string;
    submit: string;
  };
  success: {
    title: string;
    subtitle: string;
    ctaLabel: string;
  };
  validation: {
    name: string;
    email: string;
    message: string;
    general: string;
  };
};

const ENGLISH_TRANSLATION: TranslationContent = {
  ctaLabel: 'Help',
  title: 'ASK US ANYTHING',
  fields: {
    name: 'name',
    email: 'email address',
    message: 'how can we help?',
    submit: 'send',
  },
  success: {
    title: 'Thanks',
    subtitle: "Got it! We'll get back to you ASAP",
    ctaLabel: 'ok',
  },
  validation: {
    name: 'Should be at least 2 chars long',
    email: 'Please enter a valid email',
    message: 'Should be at least 2 chars long',
    general: 'Oops... Something went wrong. Please try again.',
  },
};

const TRANSLATION = {
  'en-US': ENGLISH_TRANSLATION,
  'en-DE': ENGLISH_TRANSLATION,
  'en-GB': ENGLISH_TRANSLATION,
  'en-NL': ENGLISH_TRANSLATION,
  'en-FR': ENGLISH_TRANSLATION,
  'de-DE': {
    ctaLabel: 'Hilfe',
    title: 'Das Geheimnis hinter der Schadenszahlung',
    fields: {
      name: 'Voller Name',
      email: 'E-Mail-Adresse',
      message: 'Wie können wir helfen?',
      submit: 'Senden',
    },
    success: {
      title: 'Danke',
      subtitle: 'Wir haben deine Nachricht erhalten und melden uns in Kürze bei dir.',
      ctaLabel: 'ok',
    },
    validation: {
      name: 'Sollte mind. 2 Zeichen lang sein',
      email: 'Bitte gib eine gültige E-Mail ein',
      message: 'Sollte mind. 2 Zeichen lang sein',
      general: 'Ups... Etwas ist schief gelaufen. Probier erneut.',
    },
  },
  'fr-FR': {
    ctaLabel: 'Aide',
    title: 'Le secret derrière nos\nremboursements',
    fields: {
      name: 'Nom et Prénom',
      email: 'Adresse email',
      message: 'Comment peut-on vous aider ?',
      submit: 'Envoyer',
    },
    success: {
      title: 'Merci,',
      subtitle: 'Nous reviendrons vers vous au plus vit.',
      ctaLabel: 'ok',
    },
    validation: {
      name: 'Doit comporter au moins 2 caractères',
      email: "Merci d'entrer une adresse email valide",
      message: 'Doit comporter au moins 2 caractères',
      general: 'Oups... Il y a eu une erreur, merci de réessayer.',
    },
  },
  'nl-NL': {
    ctaLabel: 'Hulp',
    title: 'Stel hier je vragen',
    fields: {
      name: 'Volledige Naam',
      email: 'E-mailadres',
      message: 'Hoe kunnen we je helpen?',
      submit: 'Verstuur',
    },
    success: {
      title: 'Bedankt',
      subtitle: 'We hebben je bericht ontvangen en nemen binnenkort contact met je op.',
      ctaLabel: 'oké',
    },
    validation: {
      name: 'Moet minimaal 2 tekens lang zijn',
      email: 'Voer alsjeblieft een geldig e-mailadres in',
      message: 'Moet minimaal 2 tekens lang zijn',
      general: 'Oeps... Er is iets verkeerd gegaan. Probeer het opnieuw.',
    },
  },
} as Record<Locale, TranslationContent>;

const SvgIcon: React.FC = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
    <path d="M11,12.3V13c0,0-1.8,0-2,0v-0.6c0-0.6,0.1-1.4,0.8-2.1c0.7-0.7,1.6-1.2,1.6-2.1c0-0.9-0.7-1.4-1.4-1.4 c-1.3,0-1.4,1.4-1.5,1.7H6.6C6.6,7.1,7.2,5,10,5c2.4,0,3.4,1.6,3.4,3C13.4,10.4,11,10.8,11,12.3z" />
    <circle cx="10" cy="15" r="1" />
    <path d="M10,2c4.4,0,8,3.6,8,8s-3.6,8-8,8s-8-3.6-8-8S5.6,2,10,2 M10,0C4.5,0,0,4.5,0,10s4.5,10,10,10s10-4.5,10-10S15.5,0,10,0 L10,0z" />
  </svg>
);

export const AskUsAnything: React.FC<AskUsAnythingProps> = ({ product, page = '' }) => {
  const content = TRANSLATION[getLocaleFromHtml() || 'en-US'];
  const baseTrackingProps = {
    page,
    ...(product != null && { product }),
  } as Record<string, string>;
  const { help } = useContext(ModalsContext);
  const { trackEvent } = useAnalyticsContext();
  const { nav } = useNavContext();
  const { isHelpModalOpen: isDialogOpen, openHelpModal: openDialog, closeHelpModal: closeDialog } = help;
  const {
    displayedErrors,
    auaState: { thankYou, submitted, submittedOnce, serverError },
    setValueOnChange,
    errors,
    values,
    onClose,
    sendButtonProps: { onClick, disabled: btnDisabled, loading: submittingForm, ...restSendButtonProps },
  } = useAuaState({
    closeDialog,
    validation: content.validation,
    trackingProps: baseTrackingProps,
  });

  const onSendButtonClick = useCallback((e: React.MouseEvent) => onClick(e, page ?? ''), [page, onClick]);

  const onClickCta = useCallback(() => {
    const path = window.location.pathname;
    let trackingProps = { ...baseTrackingProps };
    if (nav.home === path || nav.faq === path) {
      trackingProps = {
        ...trackingProps,
        location: path === nav.home ? 'HP' : 'faq',
      };
    }

    trackEvent('Ask_us_anything_popup.viewed', trackingProps);
    openDialog();
  }, [trackEvent, openDialog, nav, baseTrackingProps]);

  const onExitDialog = useCallback(() => {
    trackEvent('Ask_us_anything_popup.closed', baseTrackingProps);
    onClose();
  }, [onClose, trackEvent, baseTrackingProps]);

  const okButtonProps = useMemo(() => {
    return {
      glowing: true,
      onClick: onExitDialog,
    };
  }, [onExitDialog]);

  const [errorToDisplay] = displayedErrors;

  return (
    <>
      <ButtonWrapper>
        <CTAButton
          onClick={() => onClickCta()}
          px={[0, 0, 26]}
          py={[0, 0, 13]}
          alignItems="center"
          justifyContent="center"
        >
          <SvgIcon />
          <span>{content.ctaLabel}</span>
        </CTAButton>
      </ButtonWrapper>
      {isDialogOpen && (
        <LegacyDialog {...{ onClose: onExitDialog }}>
          <LegacyDialog.Backdrop>
            <LegacyDialog.Content>
              <LegacyDialog.CloseIcon />
              {!thankYou ? (
                <>
                  <LegacyDialog.Header>
                    <MainTitle as="h4" pb={[20, 40]}>
                      {content.title}
                    </MainTitle>
                  </LegacyDialog.Header>
                  <LegacyDialog.Body>
                    <Flex flexDirection="column" width={[297, 500]}>
                      {((errorToDisplay != null && submittedOnce) || serverError.length > 0) && (
                        <ErrorContainer>{`${errorToDisplay || serverError}`}</ErrorContainer>
                      )}
                      <Flex width={[1, 'auto']} as="form" flexDirection="column" alignItems="center" autoComplete="off">
                        <Box width={[1, 355]}>
                          <InputText
                            onChange={setValueOnChange('name')}
                            value={values.name}
                            name="name"
                            highlightError={errors?.name != null && submitted}
                            placeholder={content.fields.name}
                          />
                        </Box>
                        <Box mt={10} width={[1, 355]}>
                          <InputText
                            onChange={setValueOnChange('email')}
                            value={values.email}
                            highlightError={errors?.email != null && submitted}
                            name="email"
                            placeholder={content.fields.email}
                          />
                        </Box>
                        <Box mt={10} width={[1, 355]}>
                          <QuestionInput
                            onChange={setValueOnChange('question')}
                            value={values.question}
                            highlightError={errors?.question != null && submitted}
                            name="text"
                            style={{
                              resize: 'none',
                              overflow: 'auto',
                              borderRadius: 5,
                            }}
                            placeholder={content.fields.message}
                          />
                        </Box>
                      </Flex>
                    </Flex>
                  </LegacyDialog.Body>
                  <LegacyDialog.Footer pt={30}>
                    <PinkButton
                      {...restSendButtonProps}
                      onClick={onSendButtonClick}
                      loading={submittingForm}
                      disabled={btnDisabled || submittingForm}
                    >
                      {content.fields.submit}
                    </PinkButton>
                  </LegacyDialog.Footer>
                </>
              ) : (
                <>
                  <LegacyDialog.Header>
                    <ThankYouTitle as="h4">{`${content.success.title} ${values.name}`}</ThankYouTitle>
                  </LegacyDialog.Header>
                  <LegacyDialog.Body>
                    <SubTitle pt={[10, 20]} pb={[20, 40]} px={[0, 25]} as="p">
                      {content.success.subtitle}
                    </SubTitle>
                  </LegacyDialog.Body>
                  <LegacyDialog.Footer pt={30}>
                    <PinkButton {...okButtonProps}>{content.success.ctaLabel}</PinkButton>
                  </LegacyDialog.Footer>
                </>
              )}
            </LegacyDialog.Content>
          </LegacyDialog.Backdrop>
        </LegacyDialog>
      )}
    </>
  );
};

export default React.memo(AskUsAnything);
